import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { UserPlus, Edit2, RefreshCw, ToggleLeft, ToggleRight, Shield, ShieldOff } from 'lucide-react';

const ALL_MODULES = [
  { key: 'tickets', label: 'Tickets' },
  { key: 'customers', label: 'Kunden' },
  { key: 'time_tracking', label: 'Zeiterfassung' },
  { key: 'service_reports', label: 'Serviceberichte' },
  { key: 'devices', label: 'Geräte' },
  { key: 'journal', label: 'Journal' },
  { key: 'dr_reports', label: 'DR-Berichte' },
];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', password: '', role: 'staff', modules: []
};

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch {
      toast.error('Benutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowCreateModal(true);
  };

  const openEdit = (u) => {
    setSelectedUser(u);
    setForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, password: '', role: u.role, modules: u.modules || [] });
    setShowEditModal(true);
  };

  const toggleModule = (key) => {
    setForm(f => ({
      ...f,
      modules: f.modules.includes(key) ? f.modules.filter(m => m !== key) : [...f.modules, key]
    }));
  };

  const handleCreate = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.error('Bitte alle Pflichtfelder ausfüllen'); return;
    }
    setSaving(true);
    try {
      await api.post('/users', form);
      toast.success('Benutzer angelegt');
      setShowCreateModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, role: form.role, modules: form.modules };
      await api.patch(`/users/${selectedUser.id}`, payload);
      toast.success('Benutzer gespeichert');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}/active`, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Benutzer deaktiviert' : 'Benutzer aktiviert');
      fetchUsers();
    } catch {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`2FA für ${u.first_name} ${u.last_name} wirklich zurücksetzen?`)) return;
    try {
      await api.post(`/users/${u.id}/reset-2fa`);
      toast.success('2FA zurückgesetzt');
      fetchUsers();
    } catch {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Benutzerverwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} Benutzer</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-lg shadow-primary-500/25">
          <UserPlus size={16} /> Benutzer anlegen
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Laden...</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Rolle</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">2FA</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">{u.first_name} {u.last_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {u.role === 'admin' ? 'Admin' : 'Staff'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {u.totp_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <Shield size={11} /> Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <ShieldOff size={11} /> Nicht eingerichtet
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {u.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openEdit(u)} className="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-all" title="Bearbeiten">
                        <Edit2 size={15} />
                      </button>
                      {u.totp_enabled && (
                        <button onClick={() => reset2fa(u)} className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-all" title="2FA zurücksetzen">
                          <RefreshCw size={15} />
                        </button>
                      )}
                      {u.id !== user.id && (
                        <button onClick={() => toggleActive(u)} className={`p-1.5 rounded-lg transition-all ${u.is_active ? 'text-gray-400 hover:text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-green-500 hover:bg-green-50'}`} title={u.is_active ? 'Deaktivieren' : 'Aktivieren'}>
                          {u.is_active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-5">Benutzer anlegen</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vorname *</label>
                  <input type="text" value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nachname *</label>
                  <input type="text" value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">E-Mail *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Passwort *</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rolle</label>
                <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Module</label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_MODULES.map(m => (
                    <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.modules.includes(m.key)} onChange={() => toggleModule(m.key)} className="rounded text-primary-500" />
                      <span className="text-sm text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all">Abbrechen</button>
              <button onClick={handleCreate} disabled={saving} className="flex-1 px-4 py-2 text-sm text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all disabled:opacity-50">
                {saving ? 'Speichern...' : 'Anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-5">Benutzer bearbeiten</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vorname</label>
                  <input type="text" value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nachname</label>
                  <input type="text" value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rolle</label>
                <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Module</label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_MODULES.map(m => (
                    <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.modules.includes(m.key)} onChange={() => toggleModule(m.key)} className="rounded text-primary-500" />
                      <span className="text-sm text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all">Abbrechen</button>
              <button onClick={handleEdit} disabled={saving} className="flex-1 px-4 py-2 text-sm text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all disabled:opacity-50">
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
